import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, TrendingUp, Users, Zap } from 'lucide-react';

const Home = () => {
  const services = [
    {
      title: 'Graphic Design & Branding',
      description: 'Professional, modern visual design for businesses that want to stand out',
      icon: <Sparkles className="w-6 h-6" />,
      image: 'https://images.pexels.com/photos/16307279/pexels-photo-16307279.jpeg',
    },
    {
      title: 'Business Consultancy & Strategy',
      description: 'Insight-driven guidance to help businesses operate smarter and grow faster',
      icon: <TrendingUp className="w-6 h-6" />,
      image: 'https://images.pexels.com/photos/7654185/pexels-photo-7654185.jpeg',
    },
    {
      title: 'Digital Content & Social Media',
      description: 'Content that aligns with your brand and drives engagement',
      icon: <Users className="w-6 h-6" />,
      image: 'https://images.unsplash.com/photo-1724862936518-ae7fcfc052c1',
    },
  ];

  return (
    <div className="bg-eclipse-bg">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at top right, rgba(0, 217, 255, 0.15), transparent 40%), radial-gradient(circle at bottom left, rgba(255, 140, 66, 0.1), transparent 40%)',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1761798980621-8a451d574d24')] bg-cover bg-center opacity-5"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 text-center">
          {/* Animated Logo Video */}
          <div className="mb-12 flex justify-center" data-testid="hero-logo-video">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 object-contain"
            >
              <source
                src="https://customer-assets.emergentagent.com/job_freelance-hub-570/artifacts/288pv0s7_Animate_this_image.mp4"
                type="video/mp4"
              />
            </video>
          </div>

          {/* Overline */}
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan mb-6" data-testid="hero-overline">
            Digital Marketing Consultancy
          </p>

          {/* Heading */}
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6"
            data-testid="hero-heading"
          >
            Excellence Happens When{' '}
            <span className="bg-gradient-to-r from-brand-cyan to-brand-orange bg-clip-text text-transparent">
              Strategy Aligns
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-lg lg:text-xl text-eclipse-secondary max-w-3xl mx-auto mb-12 leading-relaxed" data-testid="hero-subheading">
            At EclipseProfessionals, we believe that excellence happens when strategy, creativity,
            and execution align perfectly — just like an eclipse.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="bg-brand-cyan text-eclipse-bg font-bold px-8 py-4 rounded-full hover:bg-white hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
            >
              Explore Services
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="bg-transparent border border-white/20 text-white hover:border-brand-orange hover:text-brand-orange rounded-full px-8 py-4 transition-all duration-300 font-medium"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan mb-4">
                About Us
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                We Don't Just Complete Tasks —{' '}
                <span className="text-brand-orange">We Solve Problems</span>
              </h2>
              <p className="text-eclipse-secondary leading-relaxed mb-6">
                We are a multidisciplinary team specializing in graphic design, consultancy, digital
                support, and business optimization. Whether you're building a brand, improving your
                operations, or elevating your online presence, we deliver work that is precise,
                professional, and impactful.
              </p>
              <p className="text-eclipse-secondary leading-relaxed mb-8">
                Clients trust us because we don't just complete tasks — we solve problems, elevate
                brands, and create systems that work. Every project is handled with dedication,
                clarity, and a commitment to measurable results.
              </p>
              <Link
                to="/about"
                data-testid="about-cta"
                className="inline-flex items-center gap-2 text-brand-cyan hover:text-brand-orange transition-colors font-semibold"
              >
                Learn More About Us
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1701486485362-2590bbfc7e00"
                alt="About EclipseProfessionals"
                className="rounded-2xl shadow-2xl shadow-brand-cyan/10"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-24 md:py-32 bg-eclipse-surface" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan mb-4">
              What We Do
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Comprehensive Digital Solutions
            </h2>
            <p className="text-eclipse-secondary max-w-2xl mx-auto leading-relaxed">
              From brand identity to business strategy, we offer end-to-end services designed to
              help your business thrive.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-eclipse-bg border border-white/5 rounded-2xl p-8 hover:-translate-y-1 hover:border-brand-cyan/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.1)] transition-all duration-300"
                data-testid={`service-card-${index + 1}`}
              >
                <div className="mb-6 overflow-hidden rounded-xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-brand-cyan/10 rounded-full text-brand-cyan">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold">{service.title}</h3>
                </div>
                <p className="text-eclipse-secondary text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="services-view-all-cta"
              className="inline-flex items-center gap-2 bg-brand-cyan text-eclipse-bg font-bold px-8 py-4 rounded-full hover:bg-white hover:scale-105 transition-all duration-300"
            >
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 md:py-32" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Why Choose{' '}
              <span className="bg-gradient-to-r from-brand-cyan to-brand-orange bg-clip-text text-transparent">
                EclipseProfessionals
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center" data-testid="feature-1">
              <div className="inline-flex p-4 bg-brand-cyan/10 rounded-full mb-6">
                <Zap className="w-8 h-8 text-brand-cyan" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Precise & Professional</h3>
              <p className="text-eclipse-secondary leading-relaxed">
                Every project is handled with dedication, clarity, and a commitment to measurable
                results.
              </p>
            </div>
            <div className="text-center" data-testid="feature-2">
              <div className="inline-flex p-4 bg-brand-orange/10 rounded-full mb-6">
                <TrendingUp className="w-8 h-8 text-brand-orange" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Results-Driven</h3>
              <p className="text-eclipse-secondary leading-relaxed">
                We focus on creating systems and strategies that deliver tangible business outcomes.
              </p>
            </div>
            <div className="text-center" data-testid="feature-3">
              <div className="inline-flex p-4 bg-brand-cyan/10 rounded-full mb-6">
                <Users className="w-8 h-8 text-brand-cyan" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Trusted Partners</h3>
              <p className="text-eclipse-secondary leading-relaxed">
                Clients trust us to solve problems, elevate brands, and create lasting impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at center, rgba(0, 217, 255, 0.1), transparent 50%)',
        }}
        data-testid="final-cta-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Ready to Make Your Business{' '}
            <span className="text-brand-orange">Stand Out?</span>
          </h2>
          <p className="text-eclipse-secondary text-lg leading-relaxed mb-8">
            Let's align your strategy, creativity, and execution perfectly. Get started with
            EclipseProfessionals today.
          </p>
          <Link
            to="/contact"
            data-testid="final-cta-button"
            className="inline-flex items-center gap-2 bg-brand-cyan text-eclipse-bg font-bold px-10 py-5 rounded-full hover:bg-white hover:scale-105 transition-all duration-300 text-lg"
          >
            Start Your Project
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
